/** @jsx h */
import { h } from "hyperapp";
import classNames from "classnames";
import nestable from "hyperapp-nestable";
import copyToClipboard from "../../../copy-to-clipboard";
import { formatHMS, parseHMS } from "../../../utilities";
import { WrappedSvg } from "../../../utilities/hyperapp";

import downArrowIcon from "!svg-inline-loader?removeSVGTagAttrs=false!../../../button/down-arrow.svg";
import noDurationLine from "!svg-inline-loader?removeSVGTagAttrs=false!../../../player/no-duration-line.svg";
import facebookIcon from "!svg-inline-loader?removeSVGTagAttrs=false!../../../share-icons/facebook.svg";
import twitterIcon from "!svg-inline-loader?removeSVGTagAttrs=false!../../../share-icons/twitter.svg";
import emailIcon from "!svg-inline-loader?removeSVGTagAttrs=false!../../../share-icons/email.svg";

const initialState = {
  tick: Date.now(),
  tickTimer: null,
  duration: null,
  actionsEl: null,
  startEl: null,
  endEl: null,
  startOffset: null,
  endOffset: null,
  dirty: false,
  startEnabled: true,
  endEnabled: false,
  showActions: false
};

function deriveFinalShareURL(
  shareURL,
  sharedMomentBaseURL,
  { startEnabled, endEnabled, startOffset, endOffset }
) {
  let finalShareURL = shareURL;
  if (sharedMomentBaseURL) {
    if (startEnabled && endEnabled) {
      finalShareURL = `${sharedMomentBaseURL}/${startOffset *
        1000}/${endOffset * 1000}`;
    } else if (startEnabled && startOffset !== 0) {
      finalShareURL = `${sharedMomentBaseURL}/${startOffset * 1000}`;
    } else if (endEnabled) {
      finalShareURL = `${sharedMomentBaseURL}/0/${endOffset * 1000}`;
    }
  }
  return finalShareURL;
}

const actions = {
  onClickOutside: e => (state, actions) => {
    const { actionsEl, showActions } = state;
    if (showActions) {
      const playerControls = document.querySelector("player-"); // This is a custom element
      const clickedOutside = !(
        actionsEl === e.target ||
        actionsEl.contains(e.target) ||
        playerControls.contains(e.target)
      );
      if (clickedOutside) {
        return {
          showActions: false
        };
      }
    }
  },
  set: partialState => partialState,
  showActions: () => (state, actions) => {
    // Reset start offset to ensure `startInput.value` gets manually updated
    window.setTimeout(() => {
      // Ensure values are reflected on recreation
      if (!state.dirty) {
        actions.setStartOffset(state.currentTime);
      } else if (state.startOffset) {
        actions.setStartOffset(state.startOffset);
      }
      if (!state.dirty) {
        actions.setEndOffset(state.currentTime);
      } else if (state.endOffset) {
        actions.setEndOffset(state.endOffset);
      }
    }, 10);
    return { showActions: true };
  },
  hideActions: () => ({ showActions: false }),
  onCreate: ({ actionsEl, duration }) => (state, actions) => {
    const tickTimer = window.setInterval(actions.tick, 1000);
    window.addEventListener("click", actions.onClickOutside);
    return {
      actionsEl,
      duration,
      tickTimer
    };
  },
  onUpdate: partialState => (state, actions) => {
    if (!state.startEnabled) {
      actions.setStartOffset(state.currentTime);
    }
    if (!state.endEnabled) {
      actions.setEndOffset(state.currentTime);
    }
    return {
      ...partialState
    };
  },
  onDestroy: () => state => {
    window.removeEventListener("click", actions.onClickOutside);
    if (state.tickTimer) {
      window.clearInterval(state.tickTimer);
    }
  },
  tick: () => ({
    tick: Date.now()
  }),
  setStartOffset: startOffset => ({ duration, startEl }) => {
    if (startOffset != null) {
      startOffset = parseHMS(startOffset);
      const showTo = duration && duration >= 3600 ? "h" : "m";
      if (startEl) {
        startEl.value = formatHMS(startOffset, showTo);
      }
      return { startOffset };
    }
  },
  setEndOffset: endOffset => ({ duration, endEl }) => {
    if (endOffset != null) {
      endOffset = parseHMS(endOffset);
      endOffset = endOffset > duration ? duration : endOffset;
      const showTo = duration && duration >= 3600 ? "h" : "m";
      if (endEl) {
        endEl.value = formatHMS(endOffset, showTo);
      }
      return { endOffset };
    }
  },
  saveStartEl: startEl => ({ duration }) => {
    return { startEl };
  },
  saveEndEl: endEl => ({ duration }) => {
    return { endEl };
  },
  onStartInputBlur: e => (state, actions) => {
    actions.setStartOffset(e.target.value);
  },
  onEndInputBlur: e => (state, actions) => {
    actions.setEndOffset(e.target.value);
  }
};

const PlayerShare = nestable(
  initialState,
  actions,
  (state, actions) => ({
    title,
    shareURL,
    sharedMomentBaseURL,
    getCurrentTime,
    label,
    duration
  }) => {
    const finalShareURL = deriveFinalShareURL(
      shareURL,
      sharedMomentBaseURL,
      state
    );

    return (
      <div
        class="player-controls__share"
        oncreate={actionsEl => {
          actions.onCreate({ actionsEl, duration });
        }}
        onupdate={() => {
          const gottenCurrentTime = getCurrentTime();
          if (
            duration !== state.duration ||
            gottenCurrentTime !== state.currentTime
          ) {
            actions.onUpdate({ duration, currentTime: gottenCurrentTime });
          }
        }}
        ondestroy={actions.onDestroy}
      >
        <div class="d-flex flex-items-v-center mt-small mb-medium">
          <WrappedSvg data={noDurationLine} />
          <button
            class="player-controls__share-button mx-xsmall d-flex flex-items-v-center"
            onclick={e => {
              e.preventDefault();
              if (state.showActions) {
                actions.hideActions();
              } else {
                actions.showActions();
              }
            }}
          >
            Share
            <div class="ml-xsmall">
              <WrappedSvg data={downArrowIcon} />
            </div>
          </button>
          <WrappedSvg data={noDurationLine} />
        </div>
        {state.showActions ? (
          <div class="dropdown__content dropdown__content--active dropdown__content--center-aligned dropdown__content--tablet-center-aligned dropdown__content--phone-center-aligned dropdown__content--player-controls-share mb-large">
            <div class="dropdown__item">
              <div class="dropdown__share d-flex flex-items-v-center">
                <span class="c-grey-darker t-alternate t-small pr-xsmall flex-1">
                  Share {label}
                </span>
                <ul class="share-icons d-flex flex-items-center">
                  <li class="share-icons__icon share-icons__icon--sm">
                    <a
                      class="d-flex flex-items-center"
                      href={`https://www.facebook.com/sharer/sharer.php?u=${shareURL}`}
                      aria-label="Share on Facebook"
                      target="_blank"
                      rel="noopener noreferrer"
                      ga-on="click,auxclick,contextmenu"
                      ga-hit-type="social"
                      ga-social-network="Facebook"
                      ga-social-action="share"
                      ga-social-target={shareURL}
                    >
                      <WrappedSvg data={facebookIcon} />
                    </a>
                  </li>
                  <li class="share-icons__icon share-icons__icon--sm">
                    <a
                      class="d-flex flex-items-center"
                      href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(
                        shareURL
                      )}&via=3RRRFM&text=${encodeURIComponent(title)}`}
                      aria-label="Share on Twitter"
                      target="_blank"
                      rel="noopener noreferrer"
                      ga-on="click,auxclick,contextmenu"
                      ga-hit-type="social"
                      ga-social-network="Twitter"
                      ga-social-action="share"
                      ga-social-target={shareURL}
                    >
                      <WrappedSvg data={twitterIcon} />
                    </a>
                  </li>
                  <li class="share-icons__icon share-icons__icon--sm">
                    <a
                      class="d-flex flex-items-center"
                      href={`mailto:?subject=From Triple R&body=${shareURL}`}
                      aria-label="Share by email"
                    >
                      <WrappedSvg data={emailIcon} />
                    </a>
                  </li>
                </ul>
              </div>
              <div
                class={classNames("dropdown__copy", {
                  "dropdown__copy--with-divider": sharedMomentBaseURL != null,
                  "mb-small": sharedMomentBaseURL != null
                })}
              >
                <div class="dropdown__copy-labels clearfix">
                  <div
                    oncreate={el => {
                      const triggerEl = el.querySelector("button");
                      const targetEl = el.querySelector("input");
                      const tooltipEl = el.querySelector(".copy-tooltip");
                      copyToClipboard(el, {
                        tooltipActiveClass: "copy-tooltip--active",
                        triggerEl,
                        targetEl,
                        tooltipEl
                      });
                    }}
                  >
                    <input
                      class="dropdown__textarea dropdown__textarea--small t-alternate mb-xsmall"
                      readonly={true}
                      value={shareURL}
                    />
                    <button class="dropdown__copy-trigger t-alternate c-red">
                      Copy URL
                      <span class="copy-tooltip">Copied</span>
                    </button>
                  </div>
                </div>
              </div>
              {sharedMomentBaseURL != null && (
                <div class="dropdown__copy">
                  <div class="d-flex flex-items-v-center mb-xsmall">
                    <span class="c-grey-darker t-alternate t-small pr-xsmall flex-1">
                      Share a moment
                    </span>
                    <ul class="share-icons d-flex flex-items-center">
                      <li class="share-icons__icon share-icons__icon--sm">
                        <a
                          class="d-flex flex-items-center"
                          href={`https://www.facebook.com/sharer/sharer.php?u=${finalShareURL}`}
                          aria-label="Share on Facebook"
                          target="_blank"
                          rel="noopener noreferrer"
                          ga-on="click,auxclick,contextmenu"
                          ga-hit-type="social"
                          ga-social-network="Facebook"
                          ga-social-action="share"
                          ga-social-target={finalShareURL}
                        >
                          <WrappedSvg data={facebookIcon} />
                        </a>
                      </li>
                      <li class="share-icons__icon share-icons__icon--sm">
                        <a
                          class="d-flex flex-items-center"
                          href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(
                            finalShareURL
                          )}&via=3RRRFM&text=${encodeURIComponent(title)}`}
                          aria-label="Share on Twitter"
                          target="_blank"
                          rel="noopener noreferrer"
                          ga-on="click,auxclick,contextmenu"
                          ga-hit-type="social"
                          ga-social-network="Twitter"
                          ga-social-action="share"
                          ga-social-target={finalShareURL}
                        >
                          <WrappedSvg data={twitterIcon} />
                        </a>
                      </li>
                      <li class="share-icons__icon share-icons__icon--sm">
                        <a
                          class="d-flex flex-items-center"
                          href={`mailto:?subject=From Triple R&body=${finalShareURL}`}
                          aria-label="Share by email"
                        >
                          <WrappedSvg data={emailIcon} />
                        </a>
                      </li>
                    </ul>
                  </div>
                  <div class="dropdown__copy-start-at d-flex flex-items-v-center">
                    <input
                      class="dropdown__copy-checkbox"
                      id="starts-at-checkbox"
                      type="checkbox"
                      oncreate={el => (el.checked = state.startEnabled)}
                      onchange={e => {
                        actions.set({ dirty: true });
                        if (e.target.checked) {
                          actions.set({ startEnabled: true });
                        } else {
                          actions.set({ startEnabled: false });
                        }
                      }}
                    />
                    <label
                      class="c-grey t-xsmall px-xsmall t-alternate"
                      for="starts-at-checkbox"
                    >
                      Start from
                    </label>
                    <input
                      oncreate={actions.saveStartEl}
                      class="dropdown__copy-start-at-time t-alternate"
                      type="text"
                      disabled={!state.startEnabled}
                      onchange={e => {
                        actions.set({ dirty: true });
                        actions.setStartOffset(e.target.value);
                      }}
                      onblur={actions.onStartInputBlur}
                    />
                  </div>
                  <div class="dropdown__copy-end-at d-flex flex-items-v-center">
                    <input
                      class="dropdown__copy-checkbox"
                      id="end-at-checkbox"
                      type="checkbox"
                      oncreate={el => (el.checked = state.endEnabled)}
                      onchange={e => {
                        actions.set({ dirty: true });
                        if (e.target.checked) {
                          if (state.endOffset === null) {
                            actions.setEndOffset(duration);
                          }
                          actions.set({ endEnabled: true });
                        } else {
                          actions.set({ endEnabled: false });
                        }
                      }}
                    />
                    <label
                      class="c-grey t-xsmall px-xsmall t-alternate"
                      for="end-at-checkbox"
                    >
                      End at
                    </label>
                    <input
                      oncreate={actions.saveEndEl}
                      class="dropdown__copy-end-at-time t-alternate"
                      type="text"
                      disabled={!state.endEnabled}
                      onchange={e => {
                        if (state.endEnabled) {
                          actions.set({ dirty: true });
                          actions.setEndOffset(e.target.value);
                        }
                      }}
                      onblur={actions.onEndInputBlur}
                    />
                  </div>
                  <div class="dropdown__copy-labels clearfix">
                    <div
                      oncreate={el => {
                        const triggerEl = el.querySelector("button");
                        const targetEl = el.querySelector(
                          ".dropdown__textarea"
                        );
                        const tooltipEl = el.querySelector(".copy-tooltip");
                        copyToClipboard(el, {
                          tooltipActiveClass: "copy-tooltip--active",
                          triggerEl,
                          targetEl,
                          tooltipEl
                        });
                      }}
                    >
                      <input
                        class="dropdown__textarea dropdown__textarea--small t-alternate mb-xsmall"
                        readonly={true}
                        value={finalShareURL}
                      />
                      <button class="dropdown__copy-trigger t-alternate c-red">
                        Copy{" "}
                        {sharedMomentBaseURL != null ? " shared moment " : ""}{" "}
                        URL
                        <span class="copy-tooltip">Copied</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div class="px-small my-small t-small">
              <a href={shareURL} onclick={e => actions.hideActions()}>
                View this item →
              </a>
            </div>
          </div>
        ) : null}
      </div>
    );
  }
);

export default PlayerShare;
